/**
 * Utilitários do painel Operação da Central (mesmas regras que desempenho / etiquetas).
 */

export const TIMEZONE_BR = 'America/Sao_Paulo'

/** Data civil (YYYY-MM-DD) no fuso do Brasil para um instante. */
export function toISODateBrazil(d) {
  const x = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(x.getTime())) return ''
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE_BR,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return fmt.format(x)
}

/**
 * Valor do b-form-datepicker: string "YYYY-MM-DD" não pode virar Date direto (vira UTC meia-noite e o dia cai um em BRT).
 * Interpreta a data civil no Brasil (meio-dia BRT) para obter o YYYY-MM-DD correto.
 */
export function isoDatePickerToBrazilISO(val) {
  if (val == null || val === '') return ''
  if (typeof val === 'string') {
    const s = val.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return toISODateBrazil(new Date(`${s}T12:00:00-03:00`))
    }
  }
  const d = val instanceof Date ? val : new Date(val)
  return toISODateBrazil(d)
}

export function brazilTodayISO() {
  return toISODateBrazil(new Date())
}

/** Soma dias a uma data civil BR (YYYY-MM-DD). */
export function addDaysBrazilISO(isoStr, delta) {
  if (!isoStr) return ''
  const t = new Date(`${isoStr}T12:00:00-03:00`)
  if (Number.isNaN(t.getTime())) return ''
  t.setDate(t.getDate() + delta)
  return toISODateBrazil(t)
}

/** Quantidade de dias no intervalo inclusivo [isoA, isoB] (datas civis BR). */
export function daysBetweenISOBr(isoA, isoB) {
  if (!isoA || !isoB) return 1
  const ta = new Date(`${isoA}T12:00:00-03:00`).getTime()
  const tb = new Date(`${isoB}T12:00:00-03:00`).getTime()
  if (Number.isNaN(ta) || Number.isNaN(tb)) return 1
  return Math.round(Math.abs(tb - ta) / 86400000) + 1
}

export function isoBrToLocalDate(iso) {
  if (!iso || typeof iso !== 'string') return null
  const p = iso.split('-').map(Number)
  if (p.length !== 3 || p.some(n => Number.isNaN(n))) return null
  return new Date(p[0], p[1] - 1, p[2])
}

/** Início do atendimento como data civil BR. */
export function inicioComoISOBrazil(at) {
  const raw = at && (at.inicio || at.created_at)
  if (!raw) return ''
  return toISODateBrazil(new Date(raw))
}

export function inicioNaFaixaBr(at, isoInicio, isoFim) {
  const d = inicioComoISOBrazil(at)
  if (!d) return false
  return d >= isoInicio && d <= isoFim
}

/** Encerramento como data civil BR (alinhado a tempo de resolução no front). */
export function fimComoISOBrazil(at) {
  const raw = at && (at.fim || at.data_resolucao || at.ultima_mensagem_em)
  if (!raw) return ''
  return toISODateBrazil(new Date(raw))
}

export function fimNaFaixaBr(at, isoInicio, isoFim) {
  const d = fimComoISOBrazil(at)
  if (!d) return false
  return d >= isoInicio && d <= isoFim
}

/**
 * Resolvidos cuja data de fim (civil BR) está em [isoIni, isoFim], deduplicados por atendimento_id.
 */
export function filterResolvidosFimNaFaixaBr(lista, isoIni, isoFim) {
  if (!isoIni || !isoFim || !Array.isArray(lista) || !lista.length) return []
  const seen = new Set()
  const out = []
  for (let i = 0; i < lista.length; i++) {
    const a = lista[i]
    if (!fimNaFaixaBr(a, isoIni, isoFim)) continue
    const id = a && a.atendimento_id
    if (id == null) continue
    if (seen.has(id)) continue
    seen.add(id)
    out.push(a)
  }
  return out
}

/**
 * Atendimento iniciado pelo respectivo atendente humano (contagem por atendimento_id).
 * Regras: atendente_ativo != null; fim_bot === null (valor JSON null; campo omitido/undefined NÃO conta);
 * atendimento_bot === false.
 */
export function iniciadaPorAtendente(at) {
  if (!at) return false
  if (at.atendimento_bot !== false) return false
  if (at.atendente_ativo == null) return false
  if (at.fim_bot !== null) return false
  const id = at.atendente_ativo.id
  return id != null && id !== ''
}

export function resolvidoPorAtendenteHumano(at) {
  if (!at || at.atendimento_bot === true) return false
  const id = at.atendente_ativo && at.atendente_ativo.id
  return id != null && id !== ''
}

export function resolvidoPorBot(at) {
  return at && at.atendimento_bot === true
}

/**
 * Quantidade de atendimentos finalizados pelo bot com data de fim (civil BR) em [isoIni, isoFim],
 * deduplicada por atendimento_id (mesma base do card "Finalizados pelo bot" na Operação Central).
 */
export function contagemFinalizadosBotNaFaixaFimBr(lista, isoIni, isoFim) {
  return filterResolvidosFimNaFaixaBr(lista || [], isoIni, isoFim).filter(resolvidoPorBot).length
}

/** Mesma base do card "Finalizados por atendente humano" na Operação Central. */
export function contagemFinalizadosHumanoNaFaixaFimBr(lista, isoIni, isoFim) {
  return filterResolvidosFimNaFaixaBr(lista || [], isoIni, isoFim).filter(resolvidoPorAtendenteHumano).length
}

export function countIniciadasPorAtendenteNoIntervalo(abertos, resolvidos, isoIni, isoFim) {
  const seen = new Set()
  let n = 0
  const bump = a => {
    if (!iniciadaPorAtendente(a)) return
    if (!inicioNaFaixaBr(a, isoIni, isoFim)) return
    const id = a.atendimento_id
    if (id == null) return
    if (seen.has(id)) return
    seen.add(id)
    n += 1
  }
  const listas = [resolvidos || [], abertos || []]
  listas.forEach(lista => lista.forEach(bump))
  return n
}

/** Atendimentos distintos cujo início (data civil BR) cai em [isoIni, isoFim]. */
export function countComInicioNoIntervalo(abertos, resolvidos, isoIni, isoFim) {
  const seen = new Set()
  let n = 0
  const bump = a => {
    if (!inicioNaFaixaBr(a, isoIni, isoFim)) return
    const id = a.atendimento_id
    if (id == null) return
    if (seen.has(id)) return
    seen.add(id)
    n += 1
  }
  const listas = [resolvidos || [], abertos || []]
  listas.forEach(lista => lista.forEach(bump))
  return n
}

/**
 * Atendimentos em aberto (lista atual da API) com início civil BR em [isoIni, isoFim], deduplicados por atendimento_id.
 */
export function countAbertosComInicioNoIntervalo(abertos, isoIni, isoFim) {
  if (!isoIni || !isoFim || !Array.isArray(abertos) || !abertos.length) return 0
  const seen = new Set()
  let n = 0
  for (let i = 0; i < abertos.length; i++) {
    const a = abertos[i]
    if (!inicioNaFaixaBr(a, isoIni, isoFim)) continue
    const id = a && a.atendimento_id
    if (id == null) continue
    if (seen.has(id)) continue
    seen.add(id)
    n += 1
  }
  return n
}

/**
 * Atendimentos distintos com início (data civil BR) no intervalo, sem bot e sem atendente ativo —
 * mesma regra da linha "Desconhecido" / sem_atendente em desempenho-atendentes (resolvidos ou em aberto na fila).
 */
export function countIniciadosSemAtendenteNoIntervalo(abertos, resolvidos, isoIni, isoFim) {
  const seen = new Set()
  let n = 0
  const bump = a => {
    if (!inicioNaFaixaBr(a, isoIni, isoFim)) return
    if (a.atendimento_bot === true) return
    const idAtt = a.atendente_ativo && a.atendente_ativo.id
    if (idAtt != null && idAtt !== '') return
    const id = a.atendimento_id
    if (id == null) return
    if (seen.has(id)) return
    seen.add(id)
    n += 1
  }
  const listas = [resolvidos || [], abertos || []]
  listas.forEach(lista => lista.forEach(bump))
  return n
}

/**
 * Lista de resolvidos cujo início (data civil BR) está em [isoIni, isoFim],
 * deduplicada por atendimento_id (mesma regra que countComInicioNoIntervalo).
 */
export function filterResolvidosInicioNaFaixaBr(lista, isoIni, isoFim) {
  if (!isoIni || !isoFim || !Array.isArray(lista) || !lista.length) return []
  const seen = new Set()
  const out = []
  for (let i = 0; i < lista.length; i++) {
    const a = lista[i]
    if (!inicioNaFaixaBr(a, isoIni, isoFim)) continue
    const id = a && a.atendimento_id
    if (id == null) continue
    if (seen.has(id)) continue
    seen.add(id)
    out.push(a)
  }
  return out
}

function hourInBrazil(raw) {
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE_BR,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(d)
  const h = parts.find(p => p.type === 'hour')
  return h != null ? Number(h.value) : null
}

/** Data dd/mm/aaaa no calendário do Brasil. */
export function formatDiaPtBRBrazil(raw) {
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIMEZONE_BR,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d)
}

export function toISODate(d) {
  const x = d instanceof Date ? d : new Date(d)
  return x.toISOString().split('T')[0]
}

export function startOfToday() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

export function endOfToday() {
  const d = new Date()
  d.setHours(23, 59, 59, 999)
  return d
}

/** Tempo em minutos até a primeira resposta (atendente), alinhado a desempenho-atendentes.vue */
export function calcularTempoPrimeiraRespostaMin(at) {
  const primeiraRespostaEm = at.primeira_mensagem_atendente_em
    ? new Date(at.primeira_mensagem_atendente_em).getTime()
    : null
  if (primeiraRespostaEm == null || Number.isNaN(primeiraRespostaEm)) return null
  const usoInicio = at.atendimento_bot === false && (at.fim_bot == null || at.fim_bot === undefined)
  let ref = null
  if (usoInicio) {
    ref = at.inicio ? new Date(at.inicio).getTime() : null
  } else if (at.fim_bot) {
    ref = new Date(at.fim_bot).getTime()
  }
  if (ref == null || Number.isNaN(ref)) return null
  return (primeiraRespostaEm - ref) / 1000 / 60
}

export function tempoResolucaoMinutos(at) {
  const inicio = at.inicio ? new Date(at.inicio).getTime() : null
  const fimBruto = at.fim || at.ultima_mensagem_em || at.ultima_mensagem?.timestamp || at.data_resolucao || at.inicio
  const fim = fimBruto ? new Date(fimBruto).getTime() : null
  if (inicio == null || fim == null || Number.isNaN(inicio) || Number.isNaN(fim)) return null
  return (fim - inicio) / 1000 / 60
}

export function average(nums) {
  const v = nums.filter(n => n != null && !Number.isNaN(n))
  if (!v.length) return null
  return v.reduce((a, b) => a + b, 0) / v.length
}

const MIN_PER_HOUR = 60
const MIN_PER_DAY = 24 * MIN_PER_HOUR

/** Parte inferior a 1 dia: até 60 min (min/s); acima de 60 min até 24h (h/min). */
function formatSubDayMinutos(minutos) {
  if (minutos == null || Number.isNaN(minutos) || minutos < 0) return ''
  if (minutos <= MIN_PER_HOUR) {
    const m = Math.floor(minutos)
    const s = Math.round((minutos - m) * 60)
    if (m <= 0) return `${Math.max(0, s)}s`
    if (s === 0) return `${m} min`
    return `${m}m ${String(s).padStart(2, '0')}s`
  }
  const h = Math.floor(minutos / MIN_PER_HOUR)
  const remMin = Math.round(minutos - h * MIN_PER_HOUR)
  if (remMin === MIN_PER_HOUR) {
    return `${h + 1}h`
  }
  if (remMin <= 0) {
    return `${h}h`
  }
  return `${h}h ${remMin}min`
}

/**
 * Duração a partir de um total em minutos (decimal).
 * Até 60 min: minutos e segundos.
 * Acima de 60 min e até 24 h: horas e minutos.
 * Acima de 24 h: dias + eventual restante (h/min ou min/s).
 */
export function formatMinutosLegivel(minutos) {
  if (minutos == null || Number.isNaN(minutos)) return '—'
  if (minutos > MIN_PER_DAY) {
    const dias = Math.floor(minutos / MIN_PER_DAY)
    const rem = minutos - dias * MIN_PER_DAY
    const diaStr = dias === 1 ? '1 dia' : `${dias} dias`
    if (rem < 0.5) return diaStr
    const resto = formatSubDayMinutos(rem)
    return resto ? `${diaStr} ${resto}` : diaStr
  }
  return formatSubDayMinutos(minutos)
}

export function formatMsLegivel(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '—'
  const minutos = ms / 60000
  return formatMinutosLegivel(minutos)
}

export function deltaPercentual(atual, anterior) {
  if (anterior == null || atual == null || anterior === 0) return null
  return Math.round(((atual - anterior) / anterior) * 100)
}

/** Agrega contagem por hora do início (0–23), horário de Brasília */
export function seriePorHoraInicio(resolvidos, filtroFn) {
  const arr = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    total: 0,
    human: 0,
    bot: 0,
  }))
  const list = (resolvidos || []).filter(filtroFn || (() => true))
  list.forEach(at => {
    const raw = at.inicio || at.created_at
    if (!raw) return
    const h = hourInBrazil(raw)
    if (h == null || h < 0 || h > 23) return
    arr[h].total += 1
    if (at.atendimento_bot === true) arr[h].bot += 1
    else arr[h].human += 1
  })
  return arr
}

export function faixaCriticaPorPicos(arr) {
  const sorted = [...arr].sort((a, b) => b.total - a.total)
  const top = sorted.slice(0, 3).map(x => x.hour).sort((a, b) => a - b)
  if (!top.length) return { label: '—', busiestHour: null }
  const busiestHour = sorted[0]?.hour
  const minH = top[0]
  const maxH = top[top.length - 1]
  let label = `${String(minH).padStart(2, '0')}h`
  if (maxH !== minH) label += ` às ${String(maxH).padStart(2, '0')}h`
  return { label, busiestHour }
}

/**
 * Série Apex heatmap (dia × hora) a partir de inícios de atendimento — mesmo critério da visão geral.
 */
export function buildHeatmapAtendimentosSeries(atendimentos, campoData = 'inicio') {
  const mapa = {}
  const diasUnicos = new Set()
  const lista = atendimentos || []
  lista.forEach(at => {
    const raw = at[campoData] || at.inicio || at.created_at
    if (!raw) return
    if (Number.isNaN(new Date(raw).getTime())) return
    const dia = formatDiaPtBRBrazil(raw)
    const h = hourInBrazil(raw)
    if (h == null) return
    const hora = `${String(h).padStart(2, '0')}h`
    diasUnicos.add(dia)
    if (!mapa[dia]) mapa[dia] = {}
    mapa[dia][hora] = (mapa[dia][hora] || 0) + 1
  })
  const ordenarDia = dia => {
    const p = dia.split('/')
    if (p.length !== 3) return 0
    return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0])).getTime()
  }
  return [...diasUnicos].sort((a, b) => ordenarDia(a) - ordenarDia(b)).map(dia => {
    const horas = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`)
    return {
      name: dia,
      data: horas.map(h => ({ x: h, y: mapa[dia]?.[h] || 0 })),
    }
  })
}

export function buildHeatmapApexOptions(series, chartId = 'oc-heatmap-atend', isMobile = false) {
  let maxValor = 0
  series.forEach(s => {
    (s.data || []).forEach(p => {
      if (p.y > maxValor) maxValor = p.y
    })
  })
  maxValor = Math.max(1, maxValor)
  const faixa1 = Math.floor(maxValor * 0.25)
  const faixa2 = Math.floor(maxValor * 0.5)
  const faixa3 = Math.floor(maxValor * 0.75)
  const fontSizeDataLabels = isMobile ? '8px' : '10px'
  const fontSizeAxis = isMobile ? '8px' : '10px'
  return {
    chart: {
      id: chartId,
      toolbar: { show: false },
      width: '100%',
      fontFamily: 'inherit',
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: fontSizeDataLabels },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.3,
        radius: 0,
        colorScale: {
          ranges: [
            {
              from: 0,
              to: faixa1,
              color: '#ede7f6',
              name: 'Baixo',
            },
            {
              from: faixa1 + 1,
              to: faixa2,
              color: '#ce93d8',
              name: 'Médio',
            },
            {
              from: faixa2 + 1,
              to: faixa3,
              color: '#ab47bc',
              name: 'Alto',
            },
            {
              from: faixa3 + 1,
              to: maxValor,
              color: '#6a1b9a',
              name: 'Muito alto',
            },
          ],
        },
      },
    },
    grid: {
      padding: { left: 4, right: 4 },
    },
    xaxis: {
      title: { text: 'Hora' },
      labels: {
        style: { fontSize: fontSizeAxis },
        maxWidth: 24,
        rotate: -45,
      },
      tickAmount: 24,
    },
    yaxis: {
      title: { text: 'Data' },
      labels: { style: { fontSize: fontSizeAxis } },
    },
    tooltip: { y: { formatter: val => `${val} atendimentos` } },
  }
}
